import React, { useEffect, useState } from "react";
import { db } from "../firebase/db";
import { collection, addDoc, onSnapshot, query, orderBy, deleteDoc, doc, serverTimestamp, writeBatch, getDocs, where, setDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import BinderView from "../components/BinderView";
import CardDetailModal from "../components/CardDetailModal";
import CardSearch from "../components/CardSearch";
import { Card } from "../types";

const TABS = ["Binder", "Inventory", "Wishlist"] as const;
type Tab = typeof TABS[number];

function getESTDateString() {
  const now = new Date();
  const estOffset = -5 * 60;
  const localOffset = now.getTimezoneOffset();
  const estDate = new Date(now.getTime() + (estOffset - localOffset) * 60 * 1000);
  return estDate.toISOString().slice(0, 10);
}

const Inventory: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("Binder");
  const [showBinderModal, setShowBinderModal] = useState(false);
  const [selectedBinder, setSelectedBinder] = useState<any>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showWishlistCardModal, setShowWishlistCardModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [binders, setBinders] = useState<any[]>([]);
  const [inventory, setInventory] = useState<Card[]>([]);
  const [wishlist, setWishlist] = useState<Card[]>([]);
  const [binderForm, setBinderForm] = useState({
    name: "",
    pages: 9,
    columns: 3,
    rows: 3,
  });
  const [cardForm, setCardForm] = useState({
    name: "",
    set: "",
    number: "",
    rarity: "",
  });

  const { user } = useAuth();
  const userId = user?.uid;

  // For Inventory tab
  const [inventoryFilter, setInventoryFilter] = useState({
    name: "",
    binderStatus: "all", // "all" | "inBinder" | "notInBinder"
  });

  // For Wishlist tab
  const [wishlistFilter, setWishlistFilter] = useState({
    name: "",
    binderStatus: "all", // "all" | "inBinder" | "notInBinder"
  });

  // Fetch binders from Firestore
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "users", userId, "binders"), orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setBinders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, [userId]);

  // Fetch inventory from Firestore
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "users", userId, "inventory"), orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setInventory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Card)));
    });
    return unsubscribe;
  }, [userId]);

  // Fetch wishlist from Firestore
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "users", userId, "wishlist"), orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setWishlist(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Card)));
    });
    return unsubscribe;
  }, [userId]);

  const handleAddBinder = async () => {
    if (!userId) return;
    const createSlots = () => {
      const slots = [];
      for (let row = 0; row < binderForm.rows; row++) {
        for (let col = 0; col < binderForm.columns; col++) {
          slots.push({ row, col, cardId: null });
        }
      }
      return slots;
    };
    const pages = Array.from({ length: binderForm.pages }, () => ({
      front: createSlots(),
      back: createSlots(),
    }));
    try {
      await addDoc(
        collection(db, "users", userId, "binders"),
        {
          name: binderForm.name,
          pages,
          columns: binderForm.columns,
          rows: binderForm.rows,
          createdAt: serverTimestamp(),
        }
      );
      setBinderForm({ name: "", pages: 9, columns: 3, rows: 3 });
      setShowBinderModal(false);
    } catch (err) {
      alert("Failed to add binder: " + (err as Error).message);
    }
  };

  const handleRemoveBinder = async (binderId: string) => {
    if (!userId) return;
    if (!window.confirm("Are you sure you want to delete this binder?")) return;
    const inventoryRef = collection(db, "users", userId, "inventory");
    const inventoryQuery = query(inventoryRef, where("assignedBinderId", "==", binderId));
    const inventorySnap = await getDocs(inventoryQuery);
    const wishlistRef = collection(db, "users", userId, "wishlist");
    const wishlistQuery = query(wishlistRef, where("assignedBinderId", "==", binderId));
    const wishlistSnap = await getDocs(wishlistQuery);
    const batch = writeBatch(db);
    inventorySnap.forEach(docSnap => {
      batch.update(docSnap.ref, {
        assignedBinderId: null,
        binderName: null,
        assignedSlot: null,
      });
    });
    wishlistSnap.forEach(docSnap => {
      batch.update(docSnap.ref, {
        assignedBinderId: null,
        binderName: null,
        assignedSlot: null,
      });
    });
    await batch.commit();
    await deleteDoc(doc(db, "users", userId, "binders", binderId));
  };

  const handleRemoveCard = async (cardId: string) => {
    if (!userId) return;
    if (!window.confirm("Are you sure you want to delete this card?")) return;
    await deleteDoc(doc(db, "users", userId, "inventory", cardId));
  };

  const handleRemoveWishlistCard = async (cardId: string) => {
    if (!userId) return;
    if (!window.confirm("Are you sure you want to delete this card from your wishlist?")) return;
    await deleteDoc(doc(db, "users", userId, "wishlist", cardId));
  };

  const handleMoveToInventory = async (card: Card) => {
    if (!userId) return;
    try {
      const { id, ...cardData } = card;
      await setDoc(doc(db, "users", userId, "inventory", id), cardData);
      await deleteDoc(doc(db, "users", userId, "wishlist", id));
    } catch (err) {
      alert("Failed to move card: " + (err as Error).message);
    }
  };

  // Inventory filter
  const filteredInventory = inventory.filter(card => {
    const matchesName = card.name.toLowerCase().includes(inventoryFilter.name.toLowerCase());
    const inBinder = !!card.assignedBinderId;
    if (inventoryFilter.binderStatus === "inBinder" && !inBinder) return false;
    if (inventoryFilter.binderStatus === "notInBinder" && inBinder) return false;
    return matchesName;
  });

  // Wishlist filter
  const filteredWishlist = wishlist.filter(card => {
    const matchesName = card.name.toLowerCase().includes(wishlistFilter.name.toLowerCase());
    const inBinder = !!card.assignedBinderId;
    if (wishlistFilter.binderStatus === "inBinder" && !inBinder) return false;
    if (wishlistFilter.binderStatus === "notInBinder" && inBinder) return false;
    return matchesName;
  });

  return (
    <div className="bg-blue-100 min-h-screen w-full">
      <div className="max-w-3xl mx-auto w-full px-2 py-4">
        {/* Tabs */}
        <div className="flex border-b mb-6 bg-white rounded-t-xl shadow-sm overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`flex-1 py-3 text-center font-semibold transition-all duration-200 outline-none
              ${activeTab === tab
                ? "border-b-4 border-indigo-500 text-indigo-700 bg-indigo-50"
                : "text-gray-500 hover:text-indigo-600 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === "Binder" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {/* Add binder slot always first */}
              <button
                onClick={() => setShowBinderModal(true)}
                className="flex flex-col items-center justify-center border-2 border-dashed border-indigo-400 rounded-xl bg-white text-indigo-400 hover:bg-indigo-50 transition-all w-36 h-36 shadow"
                aria-label="Add Binder"
              >
                <span className="text-3xl">＋</span>
                <span className="text-xs mt-1">Add Binder</span>
              </button>
              {binders.map((binder: any, idx: number) => (
                <div
                  key={binder.id}
                  className="bg-white rounded-xl shadow-md flex flex-col items-center justify-center text-center cursor-pointer w-36 h-36 relative border border-gray-100 hover:shadow-lg transition-all duration-200"
                  onClick={() => setSelectedBinder(binder)}
                >
                  {/* Remove binder button */}
                  <button
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-indigo-500 text-white shadow hover:bg-indigo-700 transition z-10"
                    style={{ padding: 0 }}
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveBinder(binder.id);
                    }}
                    title="Remove Binder"
                    aria-label="Remove Binder"
                  >
                    {/* X icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="font-bold text-indigo-700">{binder.name || `Binder ${idx + 1}`}</div>
                  <div className="text-xs text-gray-500">{binder.pages?.length || binder.pages} pages</div>
                  <div className="text-xs text-gray-500">
                    Slots: {binder.columns} × {binder.rows}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Inventory" && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  className="border rounded px-2 py-1 flex-1"
                  placeholder="Filter by name..."
                  value={inventoryFilter.name}
                  onChange={e => setInventoryFilter(f => ({ ...f, name: e.target.value }))}
                />
                <select
                  className="border rounded px-2 py-1"
                  value={inventoryFilter.binderStatus}
                  onChange={e => setInventoryFilter(f => ({ ...f, binderStatus: e.target.value }))}
                >
                  <option value="all">All Cards</option>
                  <option value="inBinder">In Binder</option>
                  <option value="notInBinder">Not in Binder</option>
                </select>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {/* Add card slot always first */}
                <button
                  onClick={() => setShowCardModal(true)}
                  className="bg-white rounded-xl shadow-md flex flex-col items-center justify-center border-2 border-dashed border-indigo-400 text-indigo-400 hover:bg-indigo-50 transition-all w-36 h-36 relative"
                  aria-label="Add Card"
                >
                  <span className="text-3xl">＋</span>
                  <span className="text-xs mt-1">Add Card</span>
                </button>
                {filteredInventory.map(card => (
                  <div
                    key={card.id}
                    className="bg-white rounded-xl shadow-md p-4 flex flex-col items-center relative border border-gray-100 hover:shadow-lg transition-all duration-200 cursor-pointer"
                    onClick={() => setSelectedCard(card)}
                  >
                    {/* Assigned badge */}
                    {card.assignedBinderId && (
                      <span className="absolute top-2 left-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded shadow z-10">
                        In Binder
                      </span>
                    )}
                    {/* Remove card button */}
                    <button
                      className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-indigo-500 text-white shadow hover:bg-indigo-700 transition z-10"
                      style={{ padding: 0 }}
                      onClick={e => {
                        e.stopPropagation();
                        handleRemoveCard(card.id);
                      }}
                      title="Remove Card"
                      aria-label="Remove Card"
                    >
                      {/* X icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    {card.image && (
                      <img src={card.image} alt={card.name} className="w-16 h-24 object-contain mb-2 rounded" />
                    )}
                    <div className="font-bold text-indigo-700 text-center">{card.name}</div>
                    <div className="text-xs text-gray-500">{card.set}</div>
                    <div className="text-xs text-gray-500">#{card.number}</div>
                    <div className="text-xs text-gray-500">{card.rarity}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === "Wishlist" && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  className="border rounded px-2 py-1 flex-1"
                  placeholder="Filter by name..."
                  value={wishlistFilter.name}
                  onChange={e => setWishlistFilter(f => ({ ...f, name: e.target.value }))}
                />
                <select
                  className="border rounded px-2 py-1"
                  value={wishlistFilter.binderStatus}
                  onChange={e => setWishlistFilter(f => ({ ...f, binderStatus: e.target.value }))}
                >
                  <option value="all">All Cards</option>
                  <option value="inBinder">In Binder</option>
                  <option value="notInBinder">Not in Binder</option>
                </select>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {/* Add wishlist card slot always first */}
                <button
                  onClick={() => setShowWishlistCardModal(true)}
                  className="bg-white rounded-xl shadow-md flex flex-col items-center justify-center border-2 border-dashed border-indigo-400 text-indigo-400 hover:bg-indigo-50 transition-all w-36 h-36 relative"
                  aria-label="Add Card"
                >
                  <span className="text-3xl">＋</span>
                  <span className="text-xs mt-1">Add Card</span>
                </button>
                {filteredWishlist.map(card => {
                  // Check if this card is already in inventory
                  const isInInventory = inventory.some(invCard =>
                    invCard.name === card.name &&
                    invCard.set === card.set &&
                    invCard.number === card.number
                  );

                  return (
                    <div
                      key={card.id}
                      className="bg-white rounded-xl shadow-md p-4 flex flex-col items-center relative border border-gray-100 hover:shadow-lg transition-all duration-200 cursor-pointer"
                      onClick={() => setSelectedCard(card)}
                    >
                      {/* Assigned badge */}
                      {card.assignedBinderId && (
                        <span className="absolute top-2 left-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded shadow z-10">
                          In Binder
                        </span>
                      )}
                      {/* Remove wishlist card button */}
                      <button
                        className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-indigo-500 text-white shadow hover:bg-indigo-700 transition z-10"
                        style={{ padding: 0 }}
                        onClick={e => {
                          e.stopPropagation();
                          handleRemoveWishlistCard(card.id);
                        }}
                        title="Remove Card"
                        aria-label="Remove Card"
                      >
                        {/* X icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      {card.image && (
                        <img src={card.image} alt={card.name} className="w-16 h-24 object-contain mb-2 rounded" />
                      )}
                      <div className="font-bold text-indigo-700 text-center">{card.name}</div>
                      <div className="text-xs text-gray-500">{card.set}</div>
                      <div className="text-xs text-gray-500">#{card.number}</div>
                      <div className="text-xs text-gray-500">{card.rarity}</div>
                      {/* Move to Inventory button: only show if NOT already in inventory */}
                      {!isInInventory && (
                        <button
                          className="absolute bottom-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-green-400 text-white shadow hover:bg-green-500 transition z-10"
                          style={{ padding: 0 }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleMoveToInventory(card);
                          }}
                          title="Move to Inventory"
                          aria-label="Move to Inventory"
                        >
                          {/* Plus icon */}
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14m7-7H5" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Binder Modal */}
        {showBinderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-xs mx-2">
              <h2 className="text-xl font-bold mb-4 text-indigo-700">Add Binder</h2>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Name
                <input
                  type="text"
                  className="border border-gray-300 rounded-lg w-full mt-1 px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  value={binderForm.name}
                  onChange={e => setBinderForm({ ...binderForm, name: e.target.value.slice(0, 20) })}
                  placeholder="Binder name"
                  maxLength={20}
                />
              </label>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Pages
                <input
                  type="number"
                  className="border border-gray-300 rounded-lg w-full mt-1 px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                  value={binderForm.pages}
                  min={1}
                  onChange={e => setBinderForm({ ...binderForm, pages: Number(e.target.value) })}
                />
              </label>
              <div className="flex gap-2 mb-2">
                <label className="flex-1 text-sm font-medium text-gray-700">
                  Columns
                  <input
                    type="number"
                    className="border border-gray-300 rounded-lg w-full mt-1 px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                    value={binderForm.columns}
                    min={1}
                    max={4}
                    onChange={e => setBinderForm({ ...binderForm, columns: Math.min(4, Number(e.target.value)) })}
                  />
                </label>
                <label className="flex-1 text-sm font-medium text-gray-700">
                  Rows
                  <input
                    type="number"
                    className="border border-gray-300 rounded-lg w-full mt-1 px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
                    value={binderForm.rows}
                    min={1}
                    max={4}
                    onChange={e => setBinderForm({ ...binderForm, rows: Math.min(4, Number(e.target.value)) })}
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                  onClick={() => setShowBinderModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
                  onClick={handleAddBinder}
                  disabled={!binderForm.name}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Card Modal */}
        {showCardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg mx-2">
              <h2 className="text-xl font-bold mb-4 text-indigo-700">Search & Add Card</h2>
              <CardSearch
                buttonLabel="Add"
                onCardSelect={async card => {
                  if (!user) return;
                  const cardToSave = {
                    name: card.name,
                    set: card.set?.name || "",
                    number: card.number,
                    rarity: card.rarity || "",
                    image: card.images?.small || "",
                    tcgId: card.id,
                    tcgplayerUrl: card.tcgplayer?.url || "",
                    createdAt: getESTDateString(),
                  };
                  await addDoc(
                    collection(db, "users", user.uid, "inventory"),
                    cardToSave
                  );
                  setShowCardModal(false);
                }}
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                  onClick={() => setShowCardModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Wishlist Card Modal */}
        {showWishlistCardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg mx-2">
              <h2 className="text-xl font-bold mb-4 text-indigo-700">Search & Add Card to Wishlist</h2>
              <CardSearch
                buttonLabel="Wishlist"
                onCardSelect={async card => {
                  if (!user) return;
                  const cardToSave = {
                    name: card.name,
                    set: card.set?.name || "",
                    number: card.number,
                    rarity: card.rarity || "",
                    image: card.images?.small || "",
                    tcgId: card.id,
                    tcgplayerUrl: card.tcgplayer?.url || "",
                    createdAt: getESTDateString(),
                  };
                  await addDoc(
                    collection(db, "users", user.uid, "wishlist"),
                    cardToSave
                  );
                  setShowWishlistCardModal(false);
                }}
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                  onClick={() => setShowWishlistCardModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Selected Binder View */}
        {selectedBinder && (
          <BinderView
            binder={selectedBinder}
            onClose={() => setSelectedBinder(null)}
            inventory={inventory}
            wishlist={wishlist}
            onSlotClick={() => {}}
          />
        )}

        {/* Card Detail Modal */}
        {selectedCard && (
          <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
        )}
      </div>
    </div>
  );
};

export default Inventory;

